import React from 'react';
import { useParams } from 'react-router-dom';
import {
  ActionIconDropdown, Dropdown, HStack, Stack, Text,
} from '@doveit/bricks';
import RightColumnPageLayout from '../../../layouts/right-column-page-layout/RightColumnPageLayout';
import useContact from '../../hooks/use-contact/useContact';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { raise } from '../../../utils';
import GenericErrorPage from '../../../pages/errors/generic/GenericErrorPage';
import AdditionalServicesWidget from '../../containers/additional-services-widget/AdditionalServicesWidget';

import ContactAvatar from '../../components/contact-avatar/ContactAvatar';
import ProcessContact from '../../containers/process-contact/ProcessContact';
import ContactIntentsWidget from '../../containers/contact-intents-widget/ContactIntentsWidget';
import ContactRumorsWidget from '../../containers/contact-rumors-widget/ContactRumorsWidget';
import ContactWidget from '../../components/contact-widget/ContactWidget';
import ContactLeadsWidget from '../../containers/contact-leads-widget/ContactLeadsWidget';
import {
  LeadStatus, ProspectStatus, ReferenceType, RumorStatus,
} from '../../../domain/types';
import ContactProspectsWidget from '../../containers/contact-prospects-widget/ContactProspectsWidget';

import useContactProspects from '../../hooks/use-contact-prospects/useContactProspects';
import ContactSearchCriteriaWidget from '../../containers/contact-search-criteria-widget/ContactSearchCriteriaWidget';
import { buildContactExistingMatchesNotice } from '../../../utils/notice/buildContactNotice';
import NoticeBoard from '../../../notice/components/notice-board/NoticeBoard';
import { useContactSimilarMatchesNoticesData } from '../../hooks/use-contact-similar-matches-notices-data/useContactSimilarMatchesNoticesData';
import ContactPropertiesWidget from '../../containers/contact-properties-widget/ContactPropertiesWidget';
import RightColumnPageSkeleton from '../../../components/page-skeleton/RightColumnPageSkeleton';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import FeedbacksWidget from '../../../feedback/containers/feedbacks-widget/FeedbacksWidget';
import InvoicesWidget from '../../../invoice/containers/invoices-widget/InvoicesWidget';
import ContactOptInsWidget from '../../containers/contact-optins-widget/ContactOptInsWidget';
import useUserPermissions from '../../../hooks/use-user-permissions/useUserPermissions';

export const LOAD_CONTACT_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni del contatto';

const ViewContactPage: React.FC = () => {
  const { id } = useParams();
  const {
    userIsAdmin, userIsFinance, userIsCallCenter, userIsContentEditor,
  } = useRBAC();

  const userPermissions = useUserPermissions(['contacts', 'contactServices', 'contactOptIns', 'contactConcessions']);

  const isMobile = useIsDevice('mobile');

  const contactId = id || raise('Missing contact id');
  const canSeeFeedback = userIsCallCenter || userIsAdmin;
  const canUpdateFeedbackStatus = userIsCallCenter || userIsAdmin;

  const canUpsertInvoices = React.useMemo(
    () => userIsAdmin || userIsFinance,
    [userIsAdmin, userIsFinance],
  );

  const {
    data: contact, isLoading: isContactLoading, mutate: mutateContact, error: contactError,
  } = useContact(contactId);

  const { mutate: mutateProspects } = useContactProspects(contactId);

  const {
    data: contactToProcessNoticesData,
    isLoading: areContactToProcessNoticesDataLoading,
    errors: contactToProcessNoticesDataError,
  } = useContactSimilarMatchesNoticesData(contactId);

  const contactToProcessNotices = React.useMemo(() => (contactToProcessNoticesData && [buildContactExistingMatchesNotice(contactToProcessNoticesData, { isMobile })]) ?? [],
    [contactToProcessNoticesData, isMobile]);

  const canUserProcessContact = React.useMemo(() => userIsAdmin || userIsCallCenter, [userIsAdmin, userIsCallCenter]);

  const onContactUpdate = React.useCallback(() => {
    mutateContact();
  }, [mutateContact]);

  const onProspectRelatedEntityUpdate = React.useCallback(() => {
    mutateProspects();
  }, [mutateProspects]);

  if (isContactLoading) {
    return <RightColumnPageSkeleton />;
  }

  if (contactError) {
    return <GenericErrorPage title={LOAD_CONTACT_ERROR_MESSAGE} />;
  }

  return contact && (
    <RightColumnPageLayout
      aria-label="Pagina dettaglio contatto"
      title={(
        <HStack
          data-testid="page-title"
          gap={200}
          wrap="nowrap"
        >
          <ContactAvatar size="M" />
          <div>
            <Text.H4 as="div">
              {contact.name || 'Contatto'}
            </Text.H4>
            <Text.Body as="div">
              {contact.phoneNumber || contact.email}
            </Text.Body>
          </div>
        </HStack>
      )}
      actions={canUserProcessContact && (
        <ActionIconDropdown
          label="Azioni sul contatto"
          aria-label="Azioni sul contatto"
        >
          <ProcessContact contact={contact}>
            {({ disabled, processLeads }) => (
              <Dropdown.Option
                label="Processa"
                aria-label="Processa contatto"
                disabled={disabled}
                onClick={processLeads}
              />
            )}
          </ProcessContact>
        </ActionIconDropdown>
      )}
      sidePrimaryContent={(
        <ContactWidget
          contact={contact}
          canEditContact={userPermissions.contacts.canEdit()}
          canSeeAdditionalServices={userPermissions.contactServices.canSee}
          canAddAdditionalServices={userPermissions.contactServices.canAdd()}
          canDeleteAdditionalServices={userPermissions.contactServices.canDelete()}
          canSeeConcessions={userPermissions.contactConcessions.canSee}
          canAddConcessions={userPermissions.contactConcessions.canAdd()}
          canDeleteConcessions={userPermissions.contactConcessions.canDelete()}
          canSeeOptIns={userPermissions.contactOptIns.canSee}
          canAddOptIns={userPermissions.contactOptIns.canAdd()}
          canDeleteOptIns={userPermissions.contactOptIns.canDelete()}
          canViewContact={false}
          onUpdate={onContactUpdate}
        />
      )}
      sideSecondaryContent={(
        <Stack gap={300}>
          <ContactOptInsWidget
            contactId={contact.id!}
            canSeeDetails={userPermissions.contactOptIns.canSee}
            canAdd={userPermissions.contactOptIns.canAdd()}
            canDelete={userPermissions.contactOptIns.canDelete()}
          />

          <AdditionalServicesWidget
            contactId={contact.id!}
            canSeeDetails={userPermissions.contactServices.canSee}
            canAdd={userPermissions.contactServices.canAdd()}
            canDelete={userPermissions.contactServices.canDelete()}
          />
        </Stack>
      )}
      topContent={(
        <NoticeBoard
          notices={contactToProcessNotices}
          isLoading={areContactToProcessNoticesDataLoading}
          errors={contactToProcessNoticesDataError}
        />
      )}
    >
      <ContactPropertiesWidget contactId={contactId} />
      <ContactProspectsWidget
        contactId={contactId}
        excludeProspectStatus={ProspectStatus.ASSIGNMENT_CREATED}
      />
      <ContactRumorsWidget
        contactId={contactId}
        excludeRumorStatus={RumorStatus.APPOINTMENT_SCHEDULED}
        onRumorUpdate={onProspectRelatedEntityUpdate}
      />
      <ContactLeadsWidget
        contactId={contactId}
        excludeLeadStatus={LeadStatus.APPOINTMENT_SCHEDULED}
        onLeadUpdate={onProspectRelatedEntityUpdate}
      />
      <ContactIntentsWidget contactId={contactId} />
      <ContactSearchCriteriaWidget contactId={contactId} />
      <FeedbacksWidget
        filters={{
          contactId,
        }}
        canSeeFeedback={canSeeFeedback}
        canUpdateStatus={canUpdateFeedbackStatus}
      />
      {(userIsAdmin || userIsFinance || userIsContentEditor) && (
        <InvoicesWidget
          canViewDetail={canUpsertInvoices}
          canAdd={canUpsertInvoices}
          canEdit={canUpsertInvoices}
          reference={{
            type: ReferenceType.CONTACT,
            id: contactId,
          }}
        />
      )}
    </RightColumnPageLayout>
  );
};

export default ViewContactPage;
